import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';

const AdminLogin = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordFocused, setPasswordFocused] = useState(false);
  const { loginAdmin, registerFirstAdmin, adminExists, checkAdminExists, currentAdmin } = useAdmin();
  const navigate = useNavigate();

  // Check if admin already exists on component mount
  useEffect(() => {
    const checkIfAdminExists = async () => {
      await checkAdminExists();
    };
    
    checkIfAdminExists();
  }, [checkAdminExists]);

  // If admin is already logged in, redirect to admin dashboard
  useEffect(() => {
    if (currentAdmin) {
      navigate('/admin/dashboard');
    }
  }, [currentAdmin, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      await loginAdmin({ email: formData.email, password: formData.password });
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleFirstAdminRegistration = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }
    
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      await registerFirstAdmin({ 
        name: formData.name, 
        email: formData.email, 
        password: formData.password 
      });
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if password meets minimum requirements
  const isPasswordValid = formData.password.length >= 8;
  const passwordsMatch = formData.password === formData.confirmPassword;

  return (
    <div className="flex flex-col justify-center" style={{ minHeight: '100vh', padding: '3rem 1.5rem' }}>
      <div style={{ maxWidth: '28rem', margin: '0 auto', width: '100%' }}>
        <h2 className="text-3xl font-bold text-center mb-6">
          {adminExists ? 'Admin Login' : 'Create First Admin Account'}
        </h2>
      </div>

      <div style={{ maxWidth: '28rem', margin: '2rem auto 0', width: '100%' }}>
        <div className="card">
          {adminExists ? (
            // Admin Login Form
            <form className="form" onSubmit={handleLogin}>
              {error && (
                <div style={{ backgroundColor: '#FEE2E2', padding: '1rem', borderRadius: '0.375rem', marginBottom: '1.5rem' }}>
                  <p style={{ color: '#B91C1C', fontSize: '0.875rem', fontWeight: '500' }}>{error}</p>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
              </div>
              
              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`btn text-smd btn-primary ${loading ? 'btn-disabled' : ''}`}
                  style={{ width: '100%' }}
                >
                  {loading ? 'Logging in...' : 'Login'}
                </button>
              </div>
            </form>
          ) : (
            // First Admin Registration Form
            <form className="form" onSubmit={handleFirstAdminRegistration}>
              {error && (
                <div style={{ backgroundColor: '#FEE2E2', padding: '1rem', borderRadius: '0.375rem', marginBottom: '1.5rem' }}>
                  <p style={{ color: '#B91C1C', fontSize: '0.875rem', fontWeight: '500' }}>{error}</p>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Full Name
                </label>
                <div className="mt-1">
                  <input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    className="form-input"
                  />
                </div>
                {(passwordFocused || formData.password) && (
                  <div className="mt-2 text-sm">
                    <p style={{ 
                      color: isPasswordValid ? 'var(--color-green-600)' : 'var(--color-gray-500)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span style={{ 
                        display: 'inline-block', 
                        width: '16px', 
                        height: '16px', 
                        borderRadius: '50%', 
                        backgroundColor: isPasswordValid ? 'var(--color-green-500)' : 'var(--color-gray-300)',
                        color: 'white',
                        textAlign: 'center',
                        lineHeight: '16px',
                        fontSize: '12px'
                      }}>
                        {isPasswordValid ? '✓' : ''}
                      </span>
                      Must be at least 8 characters
                    </p>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">
                  Confirm Password
                </label>
                <div className="mt-1">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
                {formData.confirmPassword && (
                  <div className="mt-2 text-sm">
                    <p style={{ 
                      color: passwordsMatch ? 'var(--color-green-600)' : 'var(--color-red-600)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span style={{ 
                        display: 'inline-block', 
                        width: '16px', 
                        height: '16px', 
                        borderRadius: '50%', 
                        backgroundColor: passwordsMatch ? 'var(--color-green-500)' : 'var(--color-red-500)',
                        color: 'white',
                        textAlign: 'center',
                        lineHeight: '16px',
                        fontSize: '12px'
                      }}>
                        {passwordsMatch ? '✓' : '✗'}
                      </span>
                      {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`btn text-smd btn-primary ${loading ? 'btn-disabled' : ''}`}
                  style={{ width: '100%' }}
                >
                  {loading ? 'Creating Admin...' : 'Create Admin Account'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLogin; 